import React, { useEffect, useState, useRef } from 'react';
import { BACKEND_URL } from '../utils/backend';

function Tag({ label }) {
  const colorMap = {
    Fashion: 'bg-blue-100 text-blue-800',
    'Eat & Drink': 'bg-green-100 text-green-800',
    Housing: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold mr-2 ${colorMap[label] || 'bg-gray-100 text-gray-800'}`}>{label}</span>
  );
}

function Spinner() {
  return (
    <div className="flex justify-center items-center h-32">
      <svg className="animate-spin h-8 w-8 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
      </svg>
    </div>
  );
}

function ImageCard({ image, onClick }) {
  return (
    <div
      className="bg-white rounded-lg shadow-md overflow-hidden flex flex-col cursor-pointer transition-transform duration-200 hover:scale-105 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-blue-400"
      onClick={() => onClick(image)}
      tabIndex={0}
      role="button"
      aria-label="View image details"
      onKeyDown={e => { if (e.key === 'Enter') onClick(image); }}
    >
      <div className="w-full aspect-[2/3] bg-gray-100 flex items-center justify-center overflow-hidden">
        <img
          src={image.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
          alt={image.id}
          className="w-full h-full object-contain transition-opacity duration-500 opacity-0"
          loading="lazy"
          onLoad={e => e.target.classList.add('opacity-100')}
          onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
        />
      </div>
      <div className="p-4 flex-1 flex flex-col justify-between">
        <div className="mb-2">
          <Tag label={image.domain_label} />
          {image.domain_label === 'Fashion' && image.brand_name && (
            <span className="inline-block px-2 py-1 rounded text-xs font-semibold bg-purple-100 text-purple-800 ml-2">{image.brand_name}</span>
          )}
        </div>
      </div>
    </div>
  );
}

function ImageDetail({ image, onBack }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-lg relative">
        <button className="absolute top-2 right-2 text-xl" onClick={onBack}>&times;</button>
        <img
          src={image.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
          alt={image.id}
          className="w-full h-auto rounded mb-4"
          onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
        />
        <h2 className="text-xl font-bold mb-2">{image.id}</h2>
        <div className="mb-2">Domain: <Tag label={image.domain_label} /></div>
        {image.domain_label === 'Fashion' && image.brand_name && (
          <div className="mb-2">Brand: <span className="font-semibold">{image.brand_name}</span></div>
        )}
        <div className="mb-2">Score: {image.score ?? 'N/A'}</div>
        <div className="mb-2">Created: {image.created_at ? new Date(image.created_at).toLocaleString() : 'Unknown'}</div>
        <div className="mt-4">
          <h3 className="font-semibold">Metadata</h3>
          <pre className="bg-gray-100 rounded p-2 text-xs overflow-x-auto">{JSON.stringify(image, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
}

const DOMAIN_OPTIONS = ['Fashion', 'Eat & Drink', 'Housing'];
const BRAND_OPTIONS = [
  'Gucci', 'Prada', 'Louis Vuitton', 'Chanel', 'Dior', 'Balenciaga', 'Versace', 'Fendi', 'Burberry', 'Valentino', 'Givenchy', 'Saint Laurent', 'Hermès', 'Bottega Veneta', 'Celine', 'Alexander McQueen', 'Moschino', 'Miu Miu', 'Loewe', 'Kenzo', 'Other'
];

function ClassifyForm({ image, onSave, onCancel }) {
  const [domain, setDomain] = useState(image.domain_label === 'Unknown' ? '' : image.domain_label);
  const [brand, setBrand] = useState(image.brand_name === 'Unknown' ? '' : image.brand_name);
  const [customBrand, setCustomBrand] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      let brandToSave = brand;
      if (domain === 'Fashion' && brand === 'Other') {
        brandToSave = customBrand.trim() || 'Unknown';
      }
      const body = { domain_label: domain };
      if (domain === 'Fashion') body.brand_name = brandToSave;
      const resp = await fetch(`${BACKEND_URL}/knowledge-base/images/${image.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error('Failed to update');
      onSave();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-2xl shadow-lg p-8 w-full max-w-md relative">
        <button className="absolute top-2 right-2 text-xl" onClick={onCancel}>&times;</button>
        <h2 className="text-xl font-bold mb-4">Classify Image</h2>
        <div className="mb-4">
          <label className="block font-semibold mb-2">Domain</label>
          <select value={domain} onChange={e => { setDomain(e.target.value); setBrand(''); setCustomBrand(''); }} className="border rounded px-2 py-1 w-full">
            <option value="">Select domain</option>
            {DOMAIN_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
        {domain === 'Fashion' && (
          <div className="mb-4">
            <label className="block font-semibold mb-2">Brand</label>
            <select value={brand} onChange={e => { setBrand(e.target.value); setCustomBrand(''); }} className="border rounded px-2 py-1 w-full">
              <option value="">Select brand</option>
              {BRAND_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
            {brand === 'Other' && (
              <input
                type="text"
                className="border rounded px-2 py-1 w-full mt-2"
                placeholder="Enter brand name"
                value={customBrand}
                onChange={e => setCustomBrand(e.target.value)}
                maxLength={64}
              />
            )}
          </div>
        )}
        {error && <div className="text-red-500 mb-2">{error}</div>}
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded font-semibold w-full disabled:opacity-50"
          onClick={handleSave}
          disabled={saving || !domain || (domain === 'Fashion' && (!brand || (brand === 'Other' && !customBrand.trim())))}
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function KnowledgeBase() {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [domain, setDomain] = useState('');
  const [brand, setBrand] = useState('');
  const [brandOptions, setBrandOptions] = useState([]);
  const [classifyImage, setClassifyImage] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [rowStatus, setRowStatus] = useState({}); // { [id]: 'idle'|'saving'|'saved'|'error' }
  const statusTimeouts = useRef({});

  const refresh = () => {
    setLoading(true);
    setError(null);
    let url = `${BACKEND_URL}/knowledge-base/images`;
    const params = [];
    if (domain) params.push(`domain_label=${encodeURIComponent(domain)}`);
    if (brand) params.push(`brand_name=${encodeURIComponent(brand)}`);
    if (params.length) url += '?' + params.join('&');
    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch images');
        return res.json();
      })
      .then(data => {
        setImages(Array.isArray(data) ? data : []);
        if (domain === 'Fashion') {
          const brands = Array.from(new Set(data.filter(img => img.brand_name).map(img => img.brand_name)));
          setBrandOptions(brands);
        } else {
          setBrandOptions([]);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  };
  useEffect(refresh, [domain, brand]);

  // Persist filter state in localStorage (optional)
  useEffect(() => {
    localStorage.setItem('kb_domain', domain);
    localStorage.setItem('kb_brand', brand);
  }, [domain, brand]);
  useEffect(() => {
    setDomain(localStorage.getItem('kb_domain') || '');
    setBrand(localStorage.getItem('kb_brand') || '');
  }, []);

  const handleListClassify = async (img, newDomain, newBrand) => {
    setRowStatus(s => ({ ...s, [img.id]: 'saving' }));
    try {
      const body = { domain_label: newDomain };
      if (newDomain === 'Fashion') body.brand_name = newBrand;
      const resp = await fetch(`${BACKEND_URL}/knowledge-base/images/${img.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!resp.ok) throw new Error('Failed to update');
      // Update local state for this image only
      setImages(prev => prev.map(i => i.id === img.id ? { ...i, domain_label: newDomain, brand_name: newDomain === 'Fashion' ? newBrand : '' } : i));
      setRowStatus(s => ({ ...s, [img.id]: 'saved' }));
      // Auto-clear status after 2s
      clearTimeout(statusTimeouts.current[img.id]);
      statusTimeouts.current[img.id] = setTimeout(() => setRowStatus(s => ({ ...s, [img.id]: 'idle' })), 2000);
    } catch (err) {
      setRowStatus(s => ({ ...s, [img.id]: 'error' }));
      clearTimeout(statusTimeouts.current[img.id]);
      statusTimeouts.current[img.id] = setTimeout(() => setRowStatus(s => ({ ...s, [img.id]: 'idle' })), 2000);
    }
  };

  function ListRow({ img }) {
    const [editDomain, setEditDomain] = useState(img.domain_label);
    const [editBrand, setEditBrand] = useState(img.brand_name);
    const [customBrand, setCustomBrand] = useState('');
    const status = rowStatus[img.id] || 'idle';
    const [showPreview, setShowPreview] = useState(false);
    const thumbRef = useRef();

    // For mobile: tap toggles preview
    const handleThumbClick = e => {
      if (window.innerWidth < 640) setShowPreview(v => !v);
    };

    return (
      <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-4 p-2 sm:p-3 border-b last:border-b-0 bg-white rounded-md shadow-sm mb-2 transition-all relative">
        <div className="relative">
          <img
            ref={thumbRef}
            src={img.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
            alt={img.id}
            className="w-16 h-16 object-cover rounded bg-gray-100 flex-shrink-0 cursor-zoom-in"
            loading="lazy"
            onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
            onMouseEnter={() => setShowPreview(true)}
            onMouseLeave={() => setShowPreview(false)}
            onClick={handleThumbClick}
          />
          {showPreview && (
            <div className="absolute z-50 left-20 top-1/2 -translate-y-1/2 bg-white border border-gray-300 rounded-lg shadow-lg p-1 animate-fade-in-fast" style={{ minWidth: 160 }}>
              <img
                src={img.image_url || 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'}
                alt={img.id}
                className="w-48 h-48 object-contain rounded bg-gray-50"
                onError={e => { e.target.onerror = null; e.target.src = 'https://res.cloudinary.com/djba6ta1n/image/upload/fl_preserve_transparency/v1753526178/placeholder.jpg'; }}
              />
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{img.brand_name || <span className="text-gray-400">No Brand</span>}</div>
          <div className="text-xs text-gray-500 truncate">{img.domain_label}</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-center">
          <select
            value={editDomain}
            onChange={e => {
              setEditDomain(e.target.value);
              setEditBrand('');
              setCustomBrand('');
              if (e.target.value !== img.domain_label) handleListClassify(img, e.target.value, '');
            }}
            className="border rounded px-2 py-1 text-xs min-w-[100px] touch-manipulation"
          >
            {['Unknown', ...DOMAIN_OPTIONS].map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
          {editDomain === 'Fashion' && (
            <>
              <select
                value={editBrand}
                onChange={e => {
                  setEditBrand(e.target.value);
                  setCustomBrand('');
                  if (e.target.value !== 'Other' && e.target.value !== img.brand_name) handleListClassify(img, 'Fashion', e.target.value);
                }}
                className="border rounded px-2 py-1 text-xs min-w-[100px] touch-manipulation"
              >
                <option value="">Select brand</option>
                {BRAND_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </select>
              {editBrand === 'Other' && (
                <input
                  type="text"
                  className="border rounded px-2 py-1 text-xs min-w-[100px] mt-1 sm:mt-0"
                  placeholder="Enter brand name"
                  value={customBrand}
                  onChange={e => setCustomBrand(e.target.value)}
                  onBlur={() => {
                    if (customBrand.trim()) handleListClassify(img, 'Fashion', customBrand.trim());
                  }}
                  maxLength={64}
                />
              )}
            </>
          )}
          <span className="text-xs ml-2 min-w-[40px]">
            {status === 'saving' && <span className="text-blue-500">Saving…</span>}
            {status === 'saved' && <span className="text-green-600">Saved</span>}
            {status === 'error' && <span className="text-red-500">Error</span>}
          </span>
        </div>
        {/* Optional: Delete/Edit buttons can go here */}
      </div>
    );
  }

  if (loading) return <Spinner />;
  if (error) return <div className="p-4 text-center text-red-500">Error: {error}</div>;

  return (
    <div className="p-2 sm:p-4">
      <div className="flex items-center justify-between mb-4 sm:mb-6">
        <h1 className="text-2xl font-bold">📚 Knowledge Base</h1>
        <div className="flex gap-2 items-center">
          <button
            className={`px-3 py-1 rounded-l font-semibold border ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'} transition-colors`}
            onClick={() => setViewMode('grid')}
            aria-label="Grid View"
          >
            Grid
          </button>
          <button
            className={`px-3 py-1 rounded-r font-semibold border-l-0 border ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700'} transition-colors`}
            onClick={() => setViewMode('list')}
            aria-label="List View"
          >
            List
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 sm:gap-4 mb-4 sm:mb-6 items-center">
        <label className="font-semibold">Domain:</label>
        <select value={domain} onChange={e => { setDomain(e.target.value); setBrand(''); }} className="border rounded px-2 py-1 min-w-[120px] touch-manipulation">
          <option value="">All</option>
          {DOMAIN_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
        {domain === 'Fashion' && (
          <>
            <label className="font-semibold ml-2 sm:ml-4">Brand:</label>
            <select value={brand} onChange={e => setBrand(e.target.value)} className="border rounded px-2 py-1 min-w-[120px] touch-manipulation">
              <option value="">All</option>
              {brandOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
            </select>
          </>
        )}
      </div>
      {classifyImage && (
        <ClassifyForm
          image={classifyImage}
          onSave={() => { setClassifyImage(null); refresh(); }}
          onCancel={() => setClassifyImage(null)}
        />
      )}
      {selectedImage && <ImageDetail image={selectedImage} onBack={() => setSelectedImage(null)} />}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4 transition-all duration-300">
          {images.length === 0 ? (
            <div className="col-span-full text-center text-gray-500">No images found.</div>
          ) : (
            images.map((img, idx) => (
              <div key={img.id || idx} className="relative animate-fade-in">
                <ImageCard image={img} onClick={setSelectedImage} />
                {(img.domain_label === 'Unknown' || img.brand_name === 'Unknown') && (
                  <button
                    className="absolute top-2 right-2 bg-yellow-400 text-xs px-2 py-1 rounded shadow font-bold hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-600 touch-manipulation"
                    onClick={() => setClassifyImage(img)}
                  >
                    Classify
                  </button>
                )}
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {images.length === 0 ? (
            <div className="text-center text-gray-500">No images found.</div>
          ) : (
            images.map(img => <ListRow key={img.id} img={img} />)
          )}
        </div>
      )}
    </div>
  );
} 